import React, { useEffect, useState } from 'react';
import { Star, Check, X, Loader2, Save, Clock, Image as ImageIcon, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  discord: 'Avis Discord',
  twitter: 'Avis Twitter / X',
  x: 'Avis Twitter / X',
  tag: 'Tag serveur Discord',
};

/**
 * Admin tab — AqmOptiTool: manage review-based discount % + moderate review requests.
 * Price is managed via the existing PricingTab (aqmoptitool key).
 */
export default function AqmOptiToolTab({ token }) {
  const [config, setConfig] = useState(null);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(null);
  const [tab, setTab] = useState('pending'); // 'pending' | 'history'
  const [discInput, setDiscInput] = useState('');
  const [twInput, setTwInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [previewImg, setPreviewImg] = useState(null);
  const [grantDiscord, setGrantDiscord] = useState('');
  const [grantEmail, setGrantEmail] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantedKey, setGrantedKey] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, pendingRes, histRes] = await Promise.all([
        fetch(`${API_URL}/api/aqmoptitool/config`),
        fetch(`${API_URL}/api/aqmoptitool/admin/pending`, { headers: authHeaders }),
        fetch(`${API_URL}/api/aqmoptitool/admin/history`, { headers: authHeaders }),
      ]);
      if (cfgRes.ok) {
        const c = await cfgRes.json();
        setConfig(c);
        const sd = c.source_discounts || {};
        setDiscInput(String(sd.discord ?? c.discount_percent ?? 0));
        setTwInput(String(sd.twitter ?? 0));
        setTagInput(String(sd.tag ?? 0));
      }
      if (pendingRes.ok) setPending((await pendingRes.json()).reviews || []);
      if (histRes.ok) setHistory((await histRes.json()).reviews || []);
    } catch (e) {
      toast.error('Erreur chargement AqmOptiTool');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const manualGrant = async () => {
    const did = grantDiscord.trim();
    if (!did) { toast.error('Discord ID requis'); return; }
    setGranting(true);
    setGrantedKey(null);
    try {
      const res = await fetch(`${API_URL}/api/aqmoptitool/admin/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ discord_id: did, email: grantEmail.trim(), duration_days: 365 }),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        setGrantedKey(d.license_key);
        toast.success('Licence créée avec succès !');
        setGrantDiscord(''); setGrantEmail('');
      } else {
        toast.error(d.detail || 'Échec de la génération');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setGranting(false);
    }
  };

  const saveDiscount = async () => {
    const dv = parseInt(discInput, 10);
    const tv = parseInt(twInput, 10);
    const gv = parseInt(tagInput, 10);
    for (const v of [dv, tv, gv]) {
      if (isNaN(v) || v < 0 || v > 90) {
        toast.error('Chaque valeur doit être entre 0 et 90');
        return;
      }
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/aqmoptitool/admin/config`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount_discord: dv, discount_twitter: tv, discount_tag: gv }),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success('Réductions enregistrées');
      loadAll();
    } catch {
      toast.error('Impossible de sauvegarder');
    } finally {
      setSaving(false);
    }
  };

  const decide = async (id, action) => {
    setDecisionLoading(id);
    try {
      const res = await fetch(`${API_URL}/api/aqmoptitool/admin/reviews/${id}/decision`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error('failed');
      toast.success(action === 'approve' ? 'Avis validé' : 'Avis refusé');
      loadAll();
    } catch {
      toast.error('Erreur');
    } finally {
      setDecisionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-aqmoptitool-tab">
      {/* Config card */}
      <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-4 md:p-5">
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-5 h-5 text-cyan-400" />
          <h3 className="text-white font-semibold text-sm md:text-base">Réductions avis & tag (cumulables)</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Chaque source validée ajoute son % au total (ex : Discord 10% + Twitter 5% + Tag 8% = <span className="text-cyan-400 font-semibold">23%</span>). Le client doit envoyer un screenshot pour chaque source. Plafond : 90%.
        </p>
        <div className="mb-4">
          <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">Prix actuel</label>
          <div className="px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 text-white font-semibold inline-block">
            {config?.price ?? '—'}€
            <span className="text-xs text-gray-500 ml-2">(éditable dans l&apos;onglet Prix)</span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block">💬 Avis Discord (%)</label>
            <input
              type="number" min="0" max="90"
              value={discInput}
              onChange={(e) => setDiscInput(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 text-white focus:outline-none focus:border-cyan-500/50"
              data-testid="admin-discount-discord-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block">🐦 Avis Twitter / X (%)</label>
            <input
              type="number" min="0" max="90"
              value={twInput}
              onChange={(e) => setTwInput(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 text-white focus:outline-none focus:border-cyan-500/50"
              data-testid="admin-discount-twitter-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block">🏷️ Tag serveur Discord (%)</label>
            <input
              type="number" min="0" max="90"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 text-white focus:outline-none focus:border-cyan-500/50"
              data-testid="admin-discount-tag-input"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
          <p className="text-xs text-gray-500">
            Total max possible : <span className="text-cyan-400 font-semibold">
              {Math.min(90, (parseInt(discInput || 0, 10) || 0) + (parseInt(twInput || 0, 10) || 0) + (parseInt(tagInput || 0, 10) || 0))}%
            </span>
          </p>
          <button
            onClick={saveDiscount}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-black font-semibold text-sm"
            data-testid="admin-save-discount-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>

      {/* Manual license grant — recovery tool */}
      <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-4 md:p-5" data-testid="admin-manual-grant-card">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-5 h-5 text-cyan-400" />
          <h3 className="text-white font-semibold text-sm md:text-base">Régénérer une licence Premium</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Crée / régénère une licence AqmOptiTool pour un client (ex: si le paiement est passé mais que la licence n&apos;a pas été générée).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">Discord ID</label>
            <input
              type="text"
              value={grantDiscord}
              onChange={(e) => setGrantDiscord(e.target.value)}
              placeholder="905863923813732402"
              className="w-full px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 text-white focus:outline-none focus:border-cyan-500/50 font-mono text-sm"
              data-testid="admin-grant-discord-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">Email (optionnel)</label>
            <input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="client@mail.com"
              className="w-full px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 text-white focus:outline-none focus:border-cyan-500/50 text-sm"
              data-testid="admin-grant-email-input"
            />
          </div>
          <button
            onClick={manualGrant}
            disabled={granting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-black font-semibold text-sm"
            data-testid="admin-grant-btn"
          >
            {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Générer la licence
          </button>
        </div>
        {grantedKey && (
          <div className="mt-3 flex items-center gap-2 bg-black/40 border border-cyan-800/40 rounded-lg px-3 py-2">
            <span className="text-[11px] text-gray-400">Clé générée :</span>
            <code className="flex-1 text-cyan-300 text-sm font-mono truncate select-all" data-testid="admin-granted-key">{grantedKey}</code>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-zinc-800">
        {[
          { key: 'pending', label: `En attente (${pending.length})`, icon: Clock },
          { key: 'history', label: `Historique (${history.length})`, icon: Check },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors -mb-px ${
              tab === t.key
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
            data-testid={`admin-review-tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Pending list */}
      {tab === 'pending' && (
        <div className="space-y-3">
          {pending.length === 0 ? (
            <p className="text-center text-gray-500 text-sm py-8">Aucune demande en attente.</p>
          ) : (
            Object.values(pending.reduce((acc, r) => {
              const key = r.discord_id || r.username;
              if (!acc[key]) acc[key] = { discord_id: r.discord_id, username: r.username, reviews: [] };
              acc[key].reviews.push(r);
              return acc;
            }, {})).map(group => (
              <div
                key={group.discord_id}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
                data-testid={`admin-review-user-${group.discord_id}`}
              >
                {/* User header */}
                <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-zinc-800">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm">{group.username}</p>
                    <p className="text-xs text-gray-500 font-mono truncate">{group.discord_id}</p>
                  </div>
                  <span className="text-[11px] px-2 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-semibold whitespace-nowrap">
                    {group.reviews.length} preuve{group.reviews.length > 1 ? 's' : ''} en attente
                  </span>
                </div>

                {/* One row per source/proof */}
                <div className="space-y-2">
                  {group.reviews.map(r => (
                    <div
                      key={r.id}
                      className="flex items-center gap-3 rounded-lg bg-zinc-950/40 border border-zinc-800/70 p-2.5"
                      data-testid={`admin-review-${r.id}`}
                    >
                      {r.screenshot_b64 ? (
                        <button
                          onClick={() => setPreviewImg({ src: `data:${r.screenshot_mime || 'image/png'};base64,${r.screenshot_b64}`, name: r.username })}
                          className="flex-shrink-0"
                        >
                          <img
                            src={`data:${r.screenshot_mime || 'image/png'};base64,${r.screenshot_b64}`}
                            alt="Preuve"
                            className="w-16 h-16 object-cover rounded-lg border border-zinc-700 hover:border-cyan-500/50 transition-colors cursor-zoom-in"
                          />
                        </button>
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-center flex-shrink-0">
                          <ImageIcon className="w-6 h-6 text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-cyan-400 font-medium">{SOURCE_LABELS[r.platform] || r.platform}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {r.created_at ? new Date(r.created_at).toLocaleString('fr-FR') : '—'}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => decide(r.id, 'approve')}
                          disabled={decisionLoading === r.id}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-green-500/15 hover:bg-green-500/25 border border-green-500/40 text-green-300 text-sm font-medium disabled:opacity-50"
                          data-testid={`admin-review-approve-${r.id}`}
                        >
                          <Check className="w-4 h-4" /> <span className="hidden sm:inline">Valider</span>
                        </button>
                        <button
                          onClick={() => decide(r.id, 'reject')}
                          disabled={decisionLoading === r.id}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-300 text-sm font-medium disabled:opacity-50"
                          data-testid={`admin-review-reject-${r.id}`}
                        >
                          <X className="w-4 h-4" /> <span className="hidden sm:inline">Refuser</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* History list */}
      {tab === 'history' && (
        <div className="space-y-2">
          {history.length === 0 ? (
            <p className="text-center text-gray-500 text-sm py-8">Aucun historique.</p>
          ) : (
            history.map(r => (
              <div key={r.id} className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 flex items-center justify-between gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{r.username}</p>
                  <p className="text-[11px] text-gray-500 font-mono truncate">{r.discord_id} · {SOURCE_LABELS[r.platform] || r.platform}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                  r.status === 'approved'
                    ? 'bg-green-500/15 text-green-300 border border-green-500/30'
                    : 'bg-red-500/15 text-red-300 border border-red-500/30'
                }`}>
                  {r.status === 'approved' ? 'Validé' : 'Refusé'}
                </span>
                <span className="text-[11px] text-gray-500 whitespace-nowrap hidden md:inline">
                  {r.decided_at ? new Date(r.decided_at).toLocaleDateString('fr-FR') : '—'}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Image preview modal */}
      {previewImg && (
        <div
          className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewImg(null)}
        >
          <img src={previewImg.src} alt={previewImg.name} className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
